import React, { useEffect, useState } from 'react';
import { Video, Trash2, X, Link as LinkIcon, Play } from 'lucide-react';
import Sidebar from '../../../component/includes/sidebar';
import { galleryVideoClass, GalleryVideoContent } from '../../../services/video';

const getVideoEmbedUrl = (url: string): string => {
  try {
    const urlObj = new URL(url);
    
    // YouTube
    if (urlObj.hostname.includes('youtube.com') || urlObj.hostname.includes('youtu.be')) {
      const videoId = urlObj.hostname.includes('youtu.be') 
        ? urlObj.pathname.slice(1)
        : urlObj.searchParams.get('v');
      return `https://www.youtube.com/embed/${videoId}?autoplay=0`;
    }
    
    // Facebook, TikTok, Google Drive (tetap sama)
    if (urlObj.hostname.includes('facebook.com')) {
      return `https://www.facebook.com/plugins/video.php?href=${encodeURIComponent(url)}&show_text=false`;
    }
    if (urlObj.hostname.includes('tiktok.com')) {
      const videoId = url.split('/').pop();
      return `https://www.tiktok.com/embed/${videoId}`;
    }
    if (urlObj.hostname.includes('drive.google.com')) {
      const fileId = url.match(/[-\w]{25,}(?!.*[-\w]{25,})/);
      if (fileId) return `https://drive.google.com/file/d/${fileId[0]}/preview`;
    }
    
    return url;
  } catch {
    return url;
  }
};

export default function DashboardGalleryVideo() {
  const [video, setVideo] = useState<GalleryVideoContent | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [videoLink, setVideoLink] = useState<string>('');

  useEffect(() => {
    fetchVideo();
  }, []);

  const fetchVideo = async () => {
    try {
      const data = await galleryVideoClass.getAllGalleryVideos();
      setVideo(data[0] || null);
    } catch (err: any) {
      setError(err?.error || err?.message || 'Failed to load gallery video');
    } finally {
      setLoading(false);
    }
  };

  const validateVideoLink = (link: string): string | null => {
    if (!link) return 'Please enter a video link';
    try {
      const url = new URL(link);
      const validDomains = ['youtube.com', 'youtu.be', 'facebook.com', 'fb.watch', 'tiktok.com', 'drive.google.com'];
      if (!validDomains.some(domain => url.hostname.includes(domain))) {
        return 'Supported: YouTube, Facebook, TikTok, Google Drive';
      }
    } catch {
      return 'Please enter a valid URL';
    }
    return null;
  };

  const handleLinkChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const link = e.target.value.trim();
    setVideoLink(link);
    setError(validateVideoLink(link));
  };

  const handleUpload = async () => {
    const validationError = validateVideoLink(videoLink);
    if (validationError) {
      setError(validationError);
      return;
    }

    try {
      await galleryVideoClass.createGalleryVideo({ link_video: videoLink });
      setIsModalOpen(false);
      setVideoLink('');
      setError(null);
      fetchVideo();
    } catch (err: any) {
      setError(err?.error || err?.message || 'Failed to save video link');
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Are you sure you want to delete this video?')) return;
    try {
      await galleryVideoClass.deleteGalleryVideo(id);
      fetchVideo();
    } catch (err: any) {
      setError(err?.error || err?.message || 'Failed to delete video');
    }
  };

  const resetUpload = () => {
    setIsModalOpen(false);
    setVideoLink('');
    setError(null);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-16 w-16 border-4 border-blue-500 border-t-transparent" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-4 md:p-8">
      <div className="flex flex-col md:flex-row">
        <Sidebar />

        <div className="flex-1 p-4 md:p-6 mt-10">
          <div className="max-w-7xl mx-auto">
            {/* Header seperti di gambar */}
            <div className="bg-white rounded-2xl shadow-sm p-6 mb-8 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-gradient-to-br from-blue-600 to-purple-600 rounded-xl flex items-center justify-center">
                  <Video size={24} className="text-white" />
                </div>
                <div>
                  <h1 className="text-2xl font-bold text-gray-800">Gallery Video Dashboard</h1>
                  <p className="text-sm text-gray-500">Manage your gallery video</p>
                </div>
              </div>

              {!video && (
                <button
                  onClick={() => setIsModalOpen(true)}
                  className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-6 py-2.5 rounded-xl font-medium transition-all active:scale-95 shadow-md"
                >
                  <Video size={20} />
                  Add Video
                </button>
              )}
            </div>

            {/* Video Content */}
            <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
              {video ? (
                <div className="relative">
                  {/* Video Title Bar */}
                

                  {/* Video Player dengan Play Button Overlay */}
                  <div className="relative group">
                    <iframe
                      src={getVideoEmbedUrl(video.link_video)}
                      className="w-full aspect-video"
                      allowFullScreen
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      title="Gallery Video"
                    />
                    
                    {/* Custom YouTube Play Button Overlay (hanya tampil jika bukan embed langsung) */}
                    <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                      <div className="w-20 h-20 bg-red-600 rounded-full flex items-center justify-center shadow-2xl opacity-90 group-hover:scale-110 transition-transform">
                        <Play size={42} className="text-white ml-1" fill="white" />
                      </div>
                    </div>

                    {/* Delete Button */}
                    <button
                      onClick={() => handleDelete(video.id)}
                      className="absolute top-4 right-4 p-3 bg-red-500 hover:bg-red-600 text-white rounded-full opacity-0 group-hover:opacity-100 transition-all shadow-lg"
                    >
                      <Trash2 size={20} />
                    </button>
                  </div>

                  {/* Tombol Tonton di YouTube */}
                  <div className="p-4 flex justify-end">
                    <a
                      href={video.link_video}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-2 bg-gray-800 hover:bg-black text-white px-5 py-2.5 rounded-xl text-sm font-medium transition"
                    >
                      Tonton di 
                      <span className="text-red-500">▶️ YouTube</span>
                    </a>
                  </div>
                </div>
              ) : (
                <div className="text-center py-20 text-gray-400">
                  No video in the gallery yet
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Add Video Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6 mx-4">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold text-gray-800">Add Video Link</h2>
              <button onClick={resetUpload}>
                <X size={28} className="text-gray-400 hover:text-gray-600" />
              </button>
            </div>

            {error && (
              <div className="mb-4 p-3 bg-red-50 text-red-600 rounded-xl text-sm">
                {error}
              </div>
            )}

            <div className="mb-6">
              <div className="flex items-center border-2 border-gray-200 focus-within:border-blue-500 rounded-xl p-4 transition">
                <LinkIcon size={22} className="text-gray-400 mr-3" />
                <input
                  type="text"
                  value={videoLink}
                  onChange={handleLinkChange}
                  placeholder="Paste YouTube, Facebook, TikTok or Google Drive link"
                  className="flex-1 outline-none text-gray-700 placeholder:text-gray-400"
                />
              </div>
              <p className="text-xs text-gray-500 mt-2 pl-1">
                Supported: YouTube, Facebook, TikTok, Google Drive
              </p>
            </div>

            <div className="flex gap-3">
              <button
                onClick={resetUpload}
                className="flex-1 py-3 text-gray-600 hover:bg-gray-100 rounded-xl font-medium transition"
              >
                Cancel
              </button>
              <button
                onClick={handleUpload}
                disabled={!videoLink || !!error}
                className="flex-1 py-3 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-300 text-white rounded-xl font-medium transition flex items-center justify-center gap-2"
              >
                <LinkIcon size={20} />
                Save Video Link
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}